const stripNonDigits = (phone: string) => phone.replace(/\D/g, '');
const stripLeadingZeros = (phone: string) => phone.replace(/^0+/, '');

/**
 * Strips an Irish (353) or UK (44) country code from the start of a digits-only
 * string. Mutually exclusive: at most one prefix is removed, so a national
 * number that happens to start with "44" after a "353" strip is left intact.
 */
const stripCountryCode = (digits: string): string => {
  if (digits.startsWith('353')) return digits.slice(3);
  if (digits.startsWith('44')) return digits.slice(2);
  return digits;
};

/**
 * Normalize an Irish or UK phone number to its national-significant form
 * (digits only, no country code, no leading zeros). Returns null for empty
 * input so callers can treat missing/invalid numbers as non-matching.
 */
const toNationalDigits = (phone: string): string | null => {
  const digits = stripLeadingZeros(stripNonDigits(phone));
  if (!digits) return null;
  return stripLeadingZeros(stripCountryCode(digits)) || null;
};

/**
 * Returns true if two phone numbers refer to the same Irish or UK number,
 * ignoring formatting differences (leading zeros, +353 / 353 / 0 prefixes,
 * punctuation). Comparison is exact after normalization, so unrelated digits
 * before a country code (e.g. "111113537898765") do not produce a match.
 */
export const arePhonesEqual = (a: string, b: string): boolean => {
  const na = toNationalDigits(a);
  const nb = toNationalDigits(b);
  return na !== null && na === nb;
};

/**
 * Format a phone number with +353 or +44
 * @param {string} value - The phone number to format
 */
export const formatPhone = (value: string) => {
  if (value.startsWith('353')) {
    // prefix with a plus and add a space after 353
    return `+${value.slice(0, 3)} ${value.slice(3).trim()}`;
  } else if (value.startsWith('44')) {
    // prefix with a plus and add a space after 44
    return `+${value.slice(0, 2)} ${value.slice(2).trim()}`;
  }
  return value;
};
